/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Authentication Provider Logo Components
 * 
 * Reusable logo components for OAuth authentication providers.
 * Eliminates code duplication between sign-in and sign-up pages.
 */

import React from 'react';

export interface ProviderLogoProps {
  className?: string;
}

/**
 * Google OAuth logo component
 */
export function GoogleLogo({ className = "w-5 h-5" }: ProviderLogoProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-label="Google logo">
      <path 
        fill="currentColor" 
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path 
        fill="currentColor" 
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path 
        fill="currentColor" 
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path 
        fill="currentColor" 
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

/**
 * Microsoft OAuth logo component
 */
export function MicrosoftLogo({ className = "w-5 h-5" }: ProviderLogoProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-label="Microsoft logo">
      <path 
        fill="currentColor" 
        d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"
      />
    </svg>
  );
}

/**
 * WorkOS OAuth logo component
 */
export function WorkOSLogo({ className = "w-5 h-5" }: ProviderLogoProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-label="WorkOS logo">
      <path 
        fill="currentColor" 
        d="M12 2L2 7v10l10 5 10-5V7L12 2zm0 2.18l7.3 3.65L12 11.48 4.7 7.83 12 4.18zM4 9.18l7 3.5v7.14l-7-3.5V9.18zm9 10.64v-7.14l7-3.5v7.14l-7 3.5z"
      />
    </svg>
  );
}

/**
 * Get the appropriate logo component for a provider ID
 */
export function getProviderLogo(providerId: string, className?: string): React.ReactNode {
  switch (providerId) {
    case 'google':
      return <GoogleLogo className={className} />;
    case 'microsoft':
      return <MicrosoftLogo className={className} />;
    case 'workos':
      return <WorkOSLogo className={className} />;
    default:
      return null;
  }
}

/**
 * Provider logo mapping for easy access
 */
export const PROVIDER_LOGOS = {
  google: GoogleLogo,
  microsoft: MicrosoftLogo,
  workos: WorkOSLogo,
} as const;

export type SupportedProvider = keyof typeof PROVIDER_LOGOS;